import React, { FC } from "react";
import * as Styled from "./StockSummary.css";
import PeriodReturn from "src/components/PeriodReturn";
import { RefreshIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { hasPerformanceData } from "src/utils/performances";

interface IStockSummary {
  performance: Record<string, any>;
}

/**
 * How the smartfolio has done over every horizon, as a ticker tape.
 *
 * A READOUT, NOT A CONTROL. It briefly selected the chart's period, which
 * put the choice in two places at once; the chart's range pills own the
 * period, and this runs the whole set of returns past the reader the way a
 * market tape does. Nothing here is clickable, so nothing here competes
 * with the chart.
 *
 * FIGURES, NOT BARS. The tempting move is to encode these as bars: position
 * along a common scale is the most accurately read visual channel there is
 * (Cleveland & McGill's ranking puts it above length, angle, area and
 * colour), and numbers encode nothing. But that ranking is about comparing
 * COMMENSURABLE quantities, and these are not — 0.80% over a day and 35.40%
 * over three years are cumulative returns over nested periods of wildly
 * different length. One shared scale renders every short horizon an
 * invisible sliver; a scale each drops to "position along non-aligned
 * scales", where the comparison the bars invite is no longer supported; and
 * annualising would make them comparable by changing them, which is not ours
 * to do. The right mark for incommensurable values is the one that does not
 * imply comparability.
 *
 * ORDERED BY LENGTH. The set's only structure is that the periods get
 * longer, so they run in that order and nothing is interleaved. Year to
 * date is anchored to January rather than trailing from today, but it is
 * still never longer than a trailing year, so it belongs between the six
 * month and twelve month windows rather than parked at the end — filing it
 * after 12M put a shorter period past a longer one in the one row where
 * length is the whole arrangement.
 *
 * TWO FIGURES ARE NOT HERE. Since inception is not a horizon — its length
 * is whatever this smartfolio's age happens to be, so it is not comparable
 * with the eight beside it and reads as just another period; the chart's MAX
 * window still draws it. And a volatility is not a return at all: it has no
 * direction, so it took ink and no arrow and broke the one thing a tape of
 * toned figures is for. It sits on the performance card, beside the line it
 * is measured from.
 */

/* How fast the tape runs, in CSS pixels per second. Slow enough that a
   figure stays legible as it crosses — a tape is for noticing, and hovering
   is for reading. */
const SPEED = 42;

/* Trailing, shortest to longest, then the whole history. */
const TRAILING: { period: string; field: string }[] = [
  { period: "1D", field: "one_day_return" },
  { period: "1W", field: "one_week_return" },
  { period: "1M", field: "one_month_return" },
  { period: "3M", field: "three_months_return" },
  { period: "6M", field: "six_months_return" },
  /* Anchored to January rather than trailing from today, and so never
     longer than the twelve-month window it sits before: on any day of the
     year it covers less ground than a trailing year does. */
  { period: "YTD", field: "year2date_return" },
  { period: "12M", field: "one_year_return" },
  { period: "3Y", field: "three_years_return" },
  { period: "5Y", field: "five_years_return" },
];

export const StockSummary: FC<IStockSummary> = ({ performance }) => {
  const tape = React.useRef<HTMLDivElement>(null);
  const run = React.useRef<HTMLDivElement>(null);
  /**
   * How wide one copy is, how long it takes to cross, and how many copies
   * the rail needs.
   *
   * Everything is measured, and everything has to be: the width depends on
   * how many digits the figures happen to have and on the type the browser
   * actually used, so a duration guessed from the item count would run at a
   * different speed on every card.
   *
   * A copy is never allowed to be narrower than the bar. Nine figures do not
   * fill a 1096px card, and while a copy is narrower than the window it runs
   * through, the window necessarily shows the tail of one copy and the head
   * of the next together — the same period twice on screen, once near each
   * edge — and a shorter rail simply ran out of figures and went blank
   * before jumping back. Stretching a copy to the bar's width fixes both:
   * one boundary can be on screen at a time, so every figure showing is a
   * different figure, and two copies always cover the window.
   */
  const [tapeMetrics, setTapeMetrics] = React.useState({
    shift: 0,
    seconds: 0,
    copies: 2,
    /* What one copy is stretched to. */
    min: 0,
  });

  React.useLayoutEffect(() => {
    const runEl = run.current;
    const tapeEl = tape.current;
    if (!runEl || !tapeEl) return undefined;
    const measure = () => {
      const visible = tapeEl.clientWidth;
      /* The wider of what the figures need and what the bar shows, so the
         first measurement is already the final one and the tape does not
         start at one width and jump to another. */
      const width = Math.max(runEl.scrollWidth, visible);
      if (width <= 0) return;
      setTapeMetrics((was) => {
        const next = {
          shift: width,
          seconds: width / SPEED,
          copies: Math.ceil(visible / width) + 1,
          min: visible,
        };
        return was.shift === next.shift && was.min === next.min ? was : next;
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(runEl);
    observer.observe(tapeEl);
    return () => observer.disconnect();
  }, [performance]);

  if (!hasPerformanceData(performance)) {
    return (
      <Styled.Pending role="status">
        <HugeIcon icon={RefreshIcon} size={14} spin />
        Working out this smartfolio's returns — they will appear here shortly.
      </Styled.Pending>
    );
  }

  const runStyle = tapeMetrics.min
    ? { minWidth: `${tapeMetrics.min}px` }
    : undefined;

  const figures = (
    <>
      {TRAILING.map(({ period, field }) => (
        <Styled.Item key={period}>
          <PeriodReturn period={period} value={performance?.[field]} inline />
        </Styled.Item>
      ))}
    </>
  );

  return (
    <Styled.Tape ref={tape}>
      <Styled.Rail
        style={
          tapeMetrics.shift
            ? ({
                animationDuration: `${tapeMetrics.seconds}s`,
                "--tape-shift": `${tapeMetrics.shift}px`,
              } as React.CSSProperties)
            : undefined
        }
      >
        <Styled.Run ref={run} style={runStyle}>
          {figures}
        </Styled.Run>
        {/* The same figures over again, so the tape never runs out of tape.
            Only the first copy is the readout; the rest are the reserve. */}
        {Array.from({ length: Math.max(tapeMetrics.copies - 1, 1) }).map(
          (_unused, index) => (
            <Styled.Run key={index} style={runStyle} aria-hidden>
              {figures}
            </Styled.Run>
          ),
        )}
      </Styled.Rail>
    </Styled.Tape>
  );
};
